import { cheminRubrique, cheminTraduit } from './chemins.js';
import { paragraphes, urlPartage } from './contract/index.js';
import { formaterPeriode } from './formats.js';
import { artGallery, exhibitionEvent, newsArticle, visualArtwork } from './jsonld.js';
import { routes } from './routes.js';
import { rubriqueDeLaVue } from './vues.js';
const LONGUEUR_DESCRIPTION = 180;
function resumer(valeur) {
    const plat = paragraphes(valeur).join(' ').replace(/\s+/g, ' ').trim();
    if (plat.length <= LONGUEUR_DESCRIPTION)
        return plat;
    const coupe = plat.slice(0, LONGUEUR_DESCRIPTION);
    const espace = coupe.lastIndexOf(' ');
    return `${(espace > 0 ? coupe.slice(0, espace) : coupe).replace(/[.,;:]$/, '')}…`;
}
function image(ctx, media) {
    if (media === undefined)
        return undefined;
    return { url: ctx.absolu(urlPartage(media)), alt: ctx.t(media.alt) };
}
/**
 * `hreflang` réciproques : chaque langue active qui publie réellement la page
 * équivalente, plus `x-default` vers la langue par défaut de la galerie.
 */
function alternatives(ctx) {
    const cible = rubriqueDeLaVue(ctx.vue);
    if (cible === undefined)
        return [];
    const { actives, defaut } = ctx.siteData.galerie.langues;
    const publie = (langue, chemin) => routes(ctx.siteData, langue).some((r) => r.chemin === chemin);
    const liste = [];
    for (const langue of actives) {
        const chemin = cheminTraduit(cible.rubrique, cible.slug, langue);
        if (publie(langue, chemin))
            liste.push({ hreflang: langue, href: ctx.absolu(chemin) });
    }
    const parDefaut = cheminTraduit(cible.rubrique, cible.slug, defaut);
    if (publie(defaut, parDefaut)) {
        liste.push({ hreflang: 'x-default', href: ctx.absolu(parDefaut) });
    }
    return liste;
}
/**
 * Métadonnées de `<head>` d'une page : titre, description, liens, JSON-LD.
 * Un site non indexable (espace de test) porte `noindex, nofollow` partout.
 */
export function meta(ctx) {
    const page = metaDeLaVue(ctx);
    return ctx.siteData.options.indexable === false ? { ...page, robots: 'noindex, nofollow' } : page;
}
function metaDeLaVue(ctx) {
    const { galerie } = ctx.siteData;
    const canonical = ctx.absolu(ctx.chemin);
    const commun = { canonical, alternatives: alternatives(ctx) };
    const titre = (page) => `${page} — ${galerie.nom}`;
    switch (ctx.vue.type) {
        case 'accueil': {
            const exposition = ctx.expositionEnCours();
            const baseline = ctx.tf(galerie.baseline);
            return {
                ...commun,
                titre: baseline === undefined ? galerie.nom : `${galerie.nom} — ${baseline}`,
                description: resumer(ctx.t(galerie.description)),
                image: image(ctx, exposition?.vuesAccrochage[0] ?? ctx.oeuvres[0]?.medias[0]),
                typeOg: 'website',
                jsonLd: [artGallery(ctx)],
            };
        }
        case 'artistes':
            return {
                ...commun,
                titre: titre(ctx.l.artistes),
                description: resumer(ctx.t(galerie.description)),
                typeOg: 'website',
                jsonLd: [],
            };
        case 'artiste': {
            const artiste = ctx.artiste(ctx.vue.slug);
            if (artiste === undefined)
                return introuvable(ctx);
            return {
                ...commun,
                titre: titre(artiste.nom),
                description: resumer(ctx.t(artiste.bio)),
                image: image(ctx, artiste.portrait ?? ctx.oeuvresDeLArtiste(artiste)[0]?.medias[0]),
                typeOg: 'profile',
                jsonLd: [],
            };
        }
        case 'expositions':
            return {
                ...commun,
                titre: titre(ctx.l.expositions),
                description: resumer(ctx.t(galerie.description)),
                typeOg: 'website',
                jsonLd: [],
            };
        case 'foires':
            return {
                ...commun,
                titre: titre(ctx.l.foires),
                description: resumer(ctx.t(galerie.description)),
                typeOg: 'website',
                jsonLd: [],
            };
        case 'exposition':
        case 'foire': {
            const exposition = ctx.exposition(ctx.vue.slug);
            if (exposition === undefined)
                return introuvable(ctx);
            const periode = formaterPeriode(exposition.dateDebut, exposition.dateFin, ctx.langue);
            const texte = ctx.tf(exposition.texte);
            return {
                ...commun,
                titre: titre(ctx.t(exposition.titre)),
                description: resumer(texte ?? `${ctx.t(exposition.titre)}. ${periode}.`),
                image: image(ctx, exposition.vuesAccrochage[0]),
                typeOg: 'article',
                jsonLd: [exhibitionEvent(ctx, exposition)],
            };
        }
        case 'oeuvres':
            return {
                ...commun,
                titre: titre(ctx.l.oeuvres),
                description: resumer(ctx.t(galerie.description)),
                image: image(ctx, ctx.oeuvres[0]?.medias[0]),
                typeOg: 'website',
                jsonLd: [],
            };
        case 'oeuvre': {
            const oeuvre = ctx.oeuvre(ctx.vue.slug);
            if (oeuvre === undefined)
                return introuvable(ctx);
            const artiste = ctx.artiste(oeuvre.artiste);
            const nom = artiste === undefined ? oeuvre.titre : `${artiste.nom}, ${oeuvre.titre}`;
            return {
                ...commun,
                titre: titre(nom),
                description: resumer(ctx.tf(oeuvre.description) ?? ctx.t(oeuvre.technique)),
                image: image(ctx, oeuvre.medias[0]),
                typeOg: 'article',
                jsonLd: [visualArtwork(ctx, oeuvre)],
            };
        }
        case 'actualites':
            return {
                ...commun,
                titre: titre(ctx.l.actualites),
                description: resumer(ctx.t(galerie.description)),
                typeOg: 'website',
                jsonLd: [],
            };
        case 'actualite': {
            const actualite = ctx.actualite(ctx.vue.slug);
            if (actualite === undefined)
                return introuvable(ctx);
            return {
                ...commun,
                titre: titre(ctx.t(actualite.titre)),
                description: resumer(ctx.t(actualite.chapo)),
                image: image(ctx, actualite.media),
                typeOg: 'article',
                jsonLd: [newsArticle(ctx, actualite)],
            };
        }
        case 'la-galerie':
            return {
                ...commun,
                titre: titre(ctx.t(ctx.siteData.pages.laGalerie.titre)),
                description: resumer(ctx.t(ctx.siteData.pages.laGalerie.corps)),
                image: image(ctx, ctx.siteData.pages.laGalerie.media),
                typeOg: 'website',
                jsonLd: [artGallery(ctx)],
            };
        case 'contact':
            return {
                ...commun,
                titre: titre(ctx.t(ctx.siteData.pages.contact.titre)),
                description: resumer(ctx.tf(ctx.siteData.pages.contact.corps) ?? ctx.t(ctx.siteData.galerie.description)),
                typeOg: 'website',
                jsonLd: [artGallery(ctx)],
            };
        case 'mentions':
            return {
                ...commun,
                titre: titre(ctx.t(ctx.siteData.pages.mentions.titre)),
                description: resumer(ctx.t(ctx.siteData.pages.mentions.corps)),
                typeOg: 'website',
                jsonLd: [],
            };
        case 'confidentialite':
            return {
                ...commun,
                titre: titre(ctx.t(ctx.siteData.pages.confidentialite.titre)),
                description: resumer(ctx.t(ctx.siteData.pages.confidentialite.corps)),
                typeOg: 'website',
                jsonLd: [],
            };
        case 'plan-du-site':
            return {
                ...commun,
                titre: titre(ctx.l.planDuSite),
                description: resumer(ctx.t(galerie.description)),
                typeOg: 'website',
                jsonLd: [],
            };
        case 'introuvable':
            return introuvable(ctx);
    }
}
function introuvable(ctx) {
    return {
        titre: `${ctx.l.pageIntrouvable} — ${ctx.siteData.galerie.nom}`,
        description: ctx.l.pageIntrouvableTexte,
        canonical: ctx.absolu(cheminRubrique(ctx.langue, 'accueil')),
        alternatives: [],
        typeOg: 'website',
        jsonLd: [],
        robots: 'noindex, nofollow',
    };
}
//# sourceMappingURL=tete.js.map